import { useCallback } from 'react';

import { useFunnelState } from 'scenes/common/contexts/quoteFlowContext';
import {
  isApiError,
  isBusinessError,
  LOADING,
  makeApiError,
  makeApiLoaded,
  makeApiReloading,
} from 'scenes/common/helpers/apiData';
import sessionApi from 'scenes/common/api/session/sessionApi';

import {
  InsuranceOption,
  MotorApiResponses,
  MotorCoverType,
  MotorFormAnswer,
  MotorFormState,
  MotorFunnelState,
  MotorInsuranceType,
  MotorStepType,
  OptionalExtra,
  ReferenceData,
} from 'scenes/motor/types';
import {
  getCoverTypeByInsurance,
  getQuoteNumber,
  getQuoteReference,
  anyDriverUnder25,
  getPreselectedInsuranceType,
} from 'scenes/motor/selectors';
import {
  setCoverType,
  setFlybuysNumber,
  setHasFlybuys,
  setPolicyStartDate,
  setPromoCode,
  setCoverForDriversUnder25,
  setOptionalExtras,
} from 'scenes/motor/actions';
import { YesNoAnswer } from 'scenes/common/types';
import analytics from 'scenes/motor/analytics';

import { quoteApi } from 'scenes/motor/api';

export const useQuoteActions = (
  handleAnswer: (f: MotorFormAnswer) => void,
  referenceData: ReferenceData,
  onNextIfLocationIs: (s: MotorStepType, values?: MotorFormState) => void,
  onApiResponse: (data: Partial<MotorApiResponses>) => void
) => {
  const funnelState = useFunnelState<MotorFunnelState>();

  const handleQuoteResponse = useCallback(
    (quoteDetails) => {
      onApiResponse({
        quoteDetails,
      });
      return quoteDetails;
    },
    [onApiResponse]
  );

  const onSelectInsuranceOption = useCallback(
    (newPremium: InsuranceOption) => {
      onApiResponse({
        quoteDetails: LOADING,
      });
      quoteApi()
        .updateInsuranceOption(funnelState, newPremium.coverType, referenceData)
        .then(handleQuoteResponse)
        .then((quoteDetails) => {
          if (!isBusinessError(quoteDetails) && !isApiError(quoteDetails)) {
            handleAnswer(setCoverType(newPremium.coverType));
          }
        });
    },
    [
      onApiResponse,
      funnelState,
      referenceData,
      handleQuoteResponse,
      handleAnswer,
    ]
  );

  const onCreateQuote = useCallback(() => {
    onApiResponse({
      quoteDetails: LOADING,
    });
    quoteApi()
      .createQuote(funnelState, referenceData)
      .then(handleQuoteResponse)
      .then((quoteDetails) => {
        if (!isBusinessError(quoteDetails) && !isApiError(quoteDetails)) {
          onNextIfLocationIs(funnelState.step);
        }
      });
  }, [
    onApiResponse,
    funnelState,
    referenceData,
    handleQuoteResponse,
    onNextIfLocationIs,
  ]);

  const onUpdateQuoteDetails = useCallback(() => {
    onApiResponse({
      quoteDetails: LOADING,
    });
    quoteApi()
      .updateQuoteDetails(funnelState, referenceData)
      .then(handleQuoteResponse)
      .then((quoteDetails) => {
        if (!isBusinessError(quoteDetails) && !isApiError(quoteDetails)) {
          onNextIfLocationIs(funnelState.step);
        }
      });
  }, [
    onApiResponse,
    funnelState,
    referenceData,
    handleQuoteResponse,
    onNextIfLocationIs,
  ]);

  const onUpdatePolicyStartDate = useCallback(
    (policyStartDate: string) => {
      onApiResponse({
        quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
      });
      quoteApi()
        .updatePolicyStartDate(funnelState, policyStartDate, referenceData)
        .then(handleQuoteResponse)
        .then((quoteDetails) => {
          if (!isBusinessError(quoteDetails) && !isApiError(quoteDetails)) {
            handleAnswer(setPolicyStartDate(policyStartDate));
            onNextIfLocationIs(funnelState.step);
          }
        });
    },
    [
      onApiResponse,
      funnelState,
      referenceData,
      handleQuoteResponse,
      handleAnswer,
      onNextIfLocationIs,
    ]
  );

  const updateStateOnSwitchToComprehensiveFromThirdParty = useCallback(() => {
    handleAnswer(setCoverForDriversUnder25(YesNoAnswer.No));
    handleAnswer(setOptionalExtras([]));
  }, [handleAnswer]);

  const switchingToComprehensiveFromThirdParty = useCallback(
    (coverType: MotorCoverType) =>
      coverType === MotorCoverType.Comprehensive &&
      getPreselectedInsuranceType(funnelState) ===
        MotorInsuranceType.ThirdParty,
    [funnelState]
  );

  const onInsuranceTypeSwitch = useCallback(
    (insuranceType: MotorInsuranceType) => {
      const coverType = getCoverTypeByInsurance(insuranceType);
      const shouldResetUnder25Cover =
        !anyDriverUnder25(funnelState) &&
        switchingToComprehensiveFromThirdParty(coverType);

      onApiResponse({
        quoteDetails: LOADING,
      });
      quoteApi()
        .updateInsuranceType(
          funnelState,
          insuranceType,
          coverType,
          referenceData,
          // Removes optional extras from any payload immediately sent
          shouldResetUnder25Cover ? [OptionalExtra.Under25] : undefined
        )
        .then(handleQuoteResponse)
        .then((quoteDetails) => {
          if (!isBusinessError(quoteDetails) && !isApiError(quoteDetails)) {
            handleAnswer(setCoverType(coverType));
            if (shouldResetUnder25Cover) {
              // Makes sure the state is updated so that UI is correct
              updateStateOnSwitchToComprehensiveFromThirdParty();
            }
          }
        });
    },
    [
      onApiResponse,
      funnelState,
      referenceData,
      handleQuoteResponse,
      handleAnswer,
      updateStateOnSwitchToComprehensiveFromThirdParty,
      switchingToComprehensiveFromThirdParty,
    ]
  );

  const onSaveQuote = useCallback(
    async (emailAddress: string) => {
      onApiResponse({
        quoteDetails: makeApiReloading(funnelState.apiResponses.quoteDetails),
      });

      try {
        const saveQuoteResponse = await quoteApi().updateToSaveQuote(
          funnelState,
          emailAddress,
          referenceData
        );

        if (
          !isBusinessError(saveQuoteResponse) &&
          !isApiError(saveQuoteResponse)
        ) {
          const emailQuoteResponse = await quoteApi().updateToEmailQuote(
            funnelState,
            emailAddress,
            referenceData
          );

          if (
            !isBusinessError(saveQuoteResponse) &&
            !isApiError(emailQuoteResponse)
          ) {
            await sessionApi().unlockSession(
              getQuoteNumber(funnelState),
              getQuoteReference(funnelState)
            );
          }
          analytics().emailQuote(funnelState);
          handleQuoteResponse(emailQuoteResponse);
        }
      } catch (err) {
        analytics().error(funnelState, `Session api: ${err.message}`);

        onApiResponse({
          quoteDetails: makeApiError(
            err.message,
            funnelState.apiResponses.quoteDetails
          ),
        });
      }
    },
    [onApiResponse, funnelState, referenceData, handleQuoteResponse]
  );

  const onResumeQuote = useCallback(async () => {
    onApiResponse({
      quoteDetails: makeApiLoaded(funnelState.apiResponses.quoteDetails),
    });
  }, [onApiResponse, funnelState]);

  const onUpdatePromoCode = useCallback(
    (promoCode: string) => {
      handleAnswer(setPromoCode(promoCode));
    },
    [handleAnswer]
  );

  const onUpdateFlybuys = useCallback(
    (hasFlybuys: string, flybuysNumber?: string) =>
      flybuysNumber === undefined
        ? handleAnswer(setHasFlybuys(hasFlybuys))
        : handleAnswer(setFlybuysNumber(flybuysNumber)),
    [handleAnswer]
  );

  return {
    onSaveQuote,
    onResumeQuote,
    onCreateQuote,
    onUpdatePromoCode,
    onUpdateQuoteDetails,
    onUpdatePolicyStartDate,
    onInsuranceTypeSwitch,
    onSelectInsuranceOption,
    onUpdateFlybuys,
  };
};

export default useQuoteActions;
